import { saveAs } from "file-saver";
import ExcelJS, {
  Column,
  Table,
  TableColumnProperties,
  TableProperties,
} from "exceljs/dist/exceljs";
import axios from "axios";
import {
  createAuthenticatedRequest,
  getFullUrl,
} from "../configs/axios-export.custom";
import { getISODateWithAdjustedTimezone } from "../utils/conversion/date-converters";
import {
  DataColumnConfigProps,
  ColumnsSettingsData,
} from "../components/MaterialTable/Table";

export const MaxCharsAllowed = 32766;

export const buildExcelTable = (
  tableName: string,
  columns: TableColumnProperties[],
  rows: any[][],
): TableProperties => {
  return {
    name: tableName,
    ref: "A1",
    columns: columns,
    rows: rows,
  };
};

export const generateExcel = async (
  sheetName: string,
  fileName: string,
  dataTable: TableProperties,
) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(sheetName);
  const { table } = worksheet.addTable(dataTable) as unknown as {
    table: Table;
  }; // TS definition is wrong

  for (let i = 1; i <= dataTable.columns.length; i++) {
    const column = worksheet.getColumn(i);
    column.width = determineColumnWidth(column, table.columns[i - 1].name);
    column.alignment = { wrapText: true };
  }

  const buffer = await workbook.xlsx.writeBuffer();
  saveAsExcelFile(buffer, fileName);
};

export const generateExcelFromBlob = (file: string, fileName: string): void => {
  const url = window.URL.createObjectURL(new Blob([file]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `${fileName}.xlsx`);
  document.body.appendChild(link);
  link.click();
};

const saveAsExcelFile = (buffer: any, fileName: string): void => {
  import("file-saver").then(() => {
    let EXCEL_TYPE =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    let EXCEL_EXTENSION = ".xlsx";
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE,
    });
    saveAs(data, fileName + EXCEL_EXTENSION);
  });
};

const determineColumnWidth = (column: Column, columnName: string) => {
  const min_column_width = 15;
  const max_column_width = 60;
  let widthBasedOnContent = min_column_width;

  column.eachCell(
    (cell) =>
      (widthBasedOnContent = Math.max(
        widthBasedOnContent,
        Math.min(cell.text.length, max_column_width),
      )),
  );

  return Math.max(widthBasedOnContent, min_column_width);
};

export const getChunksFromString = (
  str: string,
  chunkSize: number,
): string[] => {
  if (str === null) return [""];
  const numChunks = Math.ceil(str.length / chunkSize);
  const chunks = new Array(numChunks);

  for (let i = 0, o = 0; i < numChunks; ++i, o += chunkSize) {
    chunks[i] = str.substr(o, chunkSize);
  }

  return chunks;
};

export const genEmptyRow = (size: number): string[] => {
  const chunks = new Array(size);

  for (let i = 0; i < size; ++i) {
    chunks[i] = "";
  }

  return chunks;
};

const converOpportunitiestDate = (date: Date) =>
  `${date.getFullYear()}-${
    date.getMonth() + 1 > 10 ? date.getMonth() + 1 : "0" + (date.getMonth() + 1)
  }-${
    date.getDate() + 1 > 10 ? date.getDate() : "0" + date.getDate()
  }T11:00:00.000Z`;

export const excelFileRequest = async (
  context,
  toDate: Date,
  fromDate: Date,
  ids: number[],
  username?: string,
) => {
  const fromDateDisplay = `${
    fromDate.getDate() + 1 > 10 ? fromDate.getDate() : "0" + fromDate.getDate()
  }-${
    fromDate.getMonth() + 1 >= 10
      ? fromDate.getMonth() + 1
      : "0" + (fromDate.getMonth() + 1)
  }-${fromDate.getFullYear()}`;
  const toDateDisplay = `${
    toDate.getDate() + 1 > 10 ? toDate.getDate() : "0" + toDate.getDate()
  }-${
    toDate.getMonth() + 1 >= 10
      ? toDate.getMonth() + 1
      : "0" + (toDate.getMonth() + 1)
  }-${toDate.getFullYear()}`;

  try {
    const { data } = await axios.post(
      getFullUrl(`/api/pipeline/downloadopportunities`, {
        useDedicatedEnvironment: true,
      }),
      {
        // start: converOpportunitiestDate(fromDate),
        start: getISODateWithAdjustedTimezone(fromDate),
        // end: converOpportunitiestDate(toDate),
        end: getISODateWithAdjustedTimezone(
          toDate,
          false,
          true,
          "T11:00:00+00:00",
        ),
        pipelineIds: ids,
        username,
      },
      { ...createAuthenticatedRequest(context), responseType: "blob" },
    );
    generateExcelFromBlob(
      data,
      ids.length === 1
        ? `Aidkonekt_pipeline_#${ids[0]}_${fromDateDisplay}_${toDateDisplay}`
        : `Aidkonekt_pipelines_${fromDateDisplay}_${toDateDisplay}`,
    );
  } catch (e) {
    throw new Error(e);
  }
};

/**
 * Orders and filters columns for export based on user settings
 * @param savedSettings - User's saved column settings (order, visibility)
 * @param defaultColumns - Default column configuration
 * @returns Ordered array of visible columns (excluding UI-only columns)
 */
export const getOrderedExportColumns = (
  savedSettings: ColumnsSettingsData[] | null | undefined,
  defaultColumns: DataColumnConfigProps[],
): DataColumnConfigProps[] => {
  // UI-only columns that should never be exported
  const NON_EXPORTABLE_PROPERTIES = [
    "actions", // Standard actions column (all modules)
    "adminActions", // Admin-only actions (Grants, Forecasts, ECD)
    "view", // View details column (Projects)
    "documents", // Documents viewer column (ECD)
  ];

  // Filter out UI-only columns
  const exportableColumns = defaultColumns.filter(
    (col) => !NON_EXPORTABLE_PROPERTIES.includes(col.propertyName),
  );

  // If no saved settings, use default columns with visible ones
  if (!savedSettings || savedSettings.length === 0) {
    return exportableColumns
      .filter((col) => col.isVisible)
      .sort((a, b) => a.order - b.order);
  }

  // Create a map of saved settings for quick lookup
  const settingsMap = new Map<string, ColumnsSettingsData>();
  savedSettings.forEach((setting) => {
    // Also filter out UI-only columns from saved settings
    if (!NON_EXPORTABLE_PROPERTIES.includes(setting.propertyName)) {
      settingsMap.set(setting.propertyName, setting);
    }
  });

  // Merge saved settings with default column config
  const mergedColumns = exportableColumns.map((col) => {
    const savedSetting = settingsMap.get(col.propertyName);
    if (savedSetting) {
      return {
        ...col,
        order: savedSetting.order,
        isVisible: savedSetting.isVisible,
      };
    }
    return col;
  });

  // Filter out hidden columns and sort by order
  return mergedColumns
    .filter((col) => col.isVisible)
    .sort((a, b) => a.order - b.order);
};

/**
 * Maps columns and data to export format based on column configuration
 * @param orderedColumns - Ordered columns to export
 * @param dataRows - Array of data records
 * @param dataAccessors - Object mapping propertyName to accessor function that extracts/formats value from record
 * @returns Object with columns array and rows array ready for buildExcelTable
 */
export const mapColumnsToExportFormat = (
  orderedColumns: DataColumnConfigProps[],
  dataRows: any[],
  dataAccessors: { [propertyName: string]: (record: any) => any },
): { columns: TableColumnProperties[]; rows: any[][] } => {
  // Build columns array for Excel table
  const columns: TableColumnProperties[] = orderedColumns.map((col) => ({
    name: col.label,
  }));

  let rows: any[][] = [];

  if (dataRows.length === 0) {
    rows.push(genEmptyRow(columns.length));
  } else {
    for (let i = 0; i < dataRows.length; i++) {
      const record = dataRows[i];
      let row: any[] = [];
      let chunkedFields: { index: number; chunks: string[] }[] = [];

      // Build row based on ordered columns
      orderedColumns.forEach((col, colIndex) => {
        const accessor = dataAccessors[col.propertyName];
        if (accessor) {
          let value = accessor(record);

          // Handle long text chunking for Excel cell limit
          if (typeof value === "string" && value.length > MaxCharsAllowed) {
            const chunks = getChunksFromString(value, MaxCharsAllowed);
            row.push(chunks[0]);
            if (chunks.length > 1) {
              chunkedFields.push({ index: colIndex, chunks: chunks.slice(1) });
            }
          } else {
            row.push(value !== null && value !== undefined ? value : "");
          }
        } else {
          // No accessor defined, try direct property access
          const value = record[col.propertyName];
          row.push(value !== null && value !== undefined ? value : "");
        }
      });

      rows.push(row);

      // Add additional rows for chunked content
      if (chunkedFields.length > 0) {
        const maxChunks = Math.max(
          ...chunkedFields.map((f) => f.chunks.length),
        );
        for (let j = 0; j < maxChunks; j++) {
          let emptyRow = genEmptyRow(columns.length);
          chunkedFields.forEach((field) => {
            if (j < field.chunks.length) {
              emptyRow[field.index] = field.chunks[j];
            }
          });
          rows.push(emptyRow);
        }
      }
    }
  }

  return { columns, rows };
};

// function exportPdf() {
//     const doc = new jsPDF(0, 0);

//     doc.autoTable(this.exportColumns, this.products);
//     doc.save('products.pdf');

//     // import("jspdf").then(jsPDF => {
//     //     import("jspdf-autotable").then(x => {
//     //         const doc = new jsPDF.default(0,0);
//     //         doc.autoTable(this.exportColumns, this.products);
//     //         doc.save('products.pdf');
//     //     })
//     // })
// }

/*

exportPdf() {
    import("jspdf").then(jsPDF => {
        import("jspdf-autotable").then(x => {
            const doc = new jsPDF.default(0,0);
            doc.autoTable(this.exportColumns, this.products);
            doc.save('products.pdf');
        })
    })
}

 <button type="button" pButton pRipple icon="pi pi-file-o" (click)="dt.exportCSV()" class="p-mr-2" pTooltip="CSV" tooltipPosition="bottom"></button>
            <button type="button" pButton pRipple icon="pi pi-file-excel" (click)="exportExcel()" class="p-button-success p-mr-2"  pTooltip="XLS" tooltipPosition="bottom"></button>
            <button type="button" pButton pRipple icon="pi pi-file-pdf" (click)="exportPdf()" class="p-button-warning p-mr-2" pTooltip="PDF" tooltipPosition="bottom"></button>
            <button type="button" pButton pRipple icon="pi pi-filter" (click)="dt.exportCSV({selectionOnly:true})" class="p-button-info p-ml-auto" pTooltip="Selection Only" tooltipPosition="bottom"></button>

*/
